import { useCallback } from "react";
import { filterSearch } from "@/lib/columnFilters";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { DossiersClient } from "@/features/dossiers/DossiersClient";
import {
  listDossiers,
  listActiveParties,
  dossiersKeys,
  type DossiersSearch,
} from "@/features/dossiers/dossiers.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

export const Route = createFileRoute("/_shell/dossiers/")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "dossiers")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): DossiersSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      q: str(search.q),
      partyId: str(search.partyId),
      status: str(search.status),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: DossiersScreen,
});

function DossiersScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();

  const canWrite = hasModule(session, "dossiers_write");

  const dossiersQuery = useQuery({
    queryKey: dossiersKeys.list(search),
    queryFn: () => listDossiers(search),
    placeholderData: keepPreviousData,
  });
  const partiesQuery = useQuery({
    queryKey: dossiersKeys.activeParties,
    queryFn: listActiveParties,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({
        search: (prev) => applyParamUpdates(prev, updates),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Dossiers">
      <DossiersClient
        initialItems={dossiersQuery.data?.data ?? []}
        pagination={
          dossiersQuery.data?.pagination ?? {
            limit: 50,
            hasNextPage: false,
            nextCursor: null,
          }
        }
        search={search}
        updateParams={updateParams}
        parties={partiesQuery.data ?? []}
        canWrite={canWrite}
        userId={session.userId}
        isFetching={dossiersQuery.isFetching}
        isLoading={dossiersQuery.isLoading}
      />
    </div>
  );
}
